import React from "react";
import {
  MapPin,
  Share2,
  ChevronRight,
  Building2,
  Trees,
  Waves,
  ShieldCheck,
  Sparkles,
  Home,
  BadgeCheck,
  Car,
  Ruler,
  Navigation,
  Dumbbell,
  Baby,
  CircleDot,
  Trophy,
  HeartPulse,
  Briefcase,
  GraduationCap,
  ShoppingBag,
  TrainFront,
  CalendarCheck,
  LockKeyhole,
  Sun,
  Wind,
  DoorOpen,
  Leaf,
  Users,
  Clock3,
  Phone,
} from "lucide-react";

import Footer from "../../HomePage/Footer";
import Navbar from "../../HomePage/Navbar";


/* ============================================================
   ONLINE PROJECT IMAGES
   Official Sanjeevini Group project visuals
============================================================ */

const hero =
  "https://sanjeevinigroups.com/wp-content/heliconia-landing/images/finals/evening.webp";

const architecture =
  "https://sanjeevinigroups.com/wp-content/heliconia-landing/images/finals/inspire-living.webp";

const lifestyle =
  "https://sanjeevinigroups.com/wp-content/heliconia-landing/images/finals/swimming-pool.webp";

const locationMap =
  "https://sanjeevinigroups.com/wp-content/heliconia-landing/images/finals/location-map-brochure.webp";

const masterplan =
  "https://sanjeevinigroups.com/wp-content/heliconia-landing/images/finals/masterplan.webp";

const nightView =
  "https://sanjeevinigroups.com/wp-content/heliconia-landing/images/finals/night.webp";

const gateView =
  "https://sanjeevinigroups.com/wp-content/heliconia-landing/images/finals/gate-elev.webp";

const wideElevation =
  "https://sanjeevinigroups.com/wp-content/heliconia-landing/images/finals/two-thirds.webp";

/* ============================================================
   PROJECT CONSTANTS
============================================================ */

const WHATSAPP_NUMBER = "919739580142";

const MAPS_URL =
  "https://maps.app.goo.gl/U6eeRR7hYbMgZ2gf8";

const PROJECT_NAME = "Sanjeevini Heliconia";

const RERA_NUMBER =
  "PRM/KA/RERA/1250/304/PR/180726/008826";

const WHATSAPP_MESSAGE = `Hi, I'm interested in Sanjeevini Heliconia.

📍 Location: Soukya Road, Whitefield, Bengaluru
📜 RERA: ${RERA_NUMBER}

🏡 2, 2.5 & 3 BHK Luxury Residences
🌿 2.67 Acres
🏢 2 Towers
🏠 221 Exclusive Residences
🌳 73% Open Space
🏊 30+ Amenities
🏛 7,000 Sq.Ft. Clubhouse

Configurations:
• 2 BHK – 1175 / 1190 Sq.Ft.
• 2.5 BHK – 1410 Sq.Ft.
• 3 BHK – 1675 / 1690 Sq.Ft.

Please share the latest price sheet, floor plans, availability and launch offers.

Google Maps:
${MAPS_URL}`;

function buildWhatsAppLink() {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    WHATSAPP_MESSAGE
  )}`;
}

/* ============================================================
   REUSABLE COMPONENTS
============================================================ */

function Frame({
  src,
  alt,
  ratio = "aspect-[4/5]",
  className = "",
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-3xl bg-gray-100 ${ratio} ${className}`}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onError={(e) => {
          e.currentTarget.style.display = "none";

          if (e.currentTarget.nextSibling) {
            e.currentTarget.nextSibling.style.display = "flex";
          }
        }}
        className="absolute inset-0 h-full w-full object-cover"
      />

      <div
        style={{ display: "none" }}
        className="absolute inset-0 items-center justify-center px-4 text-center text-xs font-medium uppercase tracking-wide text-gray-400"
      >
        {alt}
      </div>
    </div>
  );
}

function FloatingBadge({
  className = "",
  children,
}) {
  return (
    <div
      className={`absolute rounded-2xl bg-white px-5 py-3 shadow-xl shadow-gray-300/40 ${className}`}
    >
      {children}
    </div>
  );
}

function ChecklistItem({
  icon: Icon,
  children,
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-amber-50">
        <Icon
          className="h-5 w-5 text-amber-700"
          strokeWidth={2}
        />
      </div>

      <span className="text-sm font-semibold text-gray-800 sm:text-base">
        {children}
      </span>
    </div>
  );
}

function StatPill({
  value,
  label,
}) {
  return (
    <div className="rounded-2xl bg-amber-50 px-5 py-4 text-center">
      <p className="text-2xl font-extrabold text-amber-700">
        {value}
      </p>

      <p className="mt-0.5 text-xs font-medium text-gray-500">
        {label}
      </p>
    </div>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  description,
}) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">

      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-50">
        <Icon
          className="h-5 w-5 text-amber-700"
          strokeWidth={1.8}
        />
      </div>

      <h4 className="mt-4 text-base font-extrabold text-gray-900">
        {title}
      </h4>

      <p className="mt-2 text-sm leading-relaxed text-gray-600">
        {description}
      </p>

    </div>
  );
}

function ConfigurationCard({
  type,
  area,
  price,
  highlight,
}) {
  return (
    <div className="group rounded-3xl border border-gray-100 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">

      <div className="flex items-center justify-between">

        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-50">
          <Home
            className="h-5 w-5 text-amber-700"
            strokeWidth={1.8}
          />
        </div>

        <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-bold text-green-600">
          Available
        </span>

      </div>

      <p className="mt-5 text-sm font-extrabold uppercase tracking-wide text-amber-700">
        {type}
      </p>

      <p className="mt-2 text-2xl font-extrabold text-gray-900">
        {price}
      </p>

      <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
        <Ruler className="h-4 w-4 text-amber-600" />
        {area}
      </div>

      {highlight && (
        <p className="mt-3 text-xs font-semibold text-gray-500">
          {highlight}
        </p>
      )}

    </div>
  );
}

function USPItem({
  icon: Icon,
  children,
}) {
  return (
    <div className="flex items-start gap-3">

      <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-amber-50">

        <Icon
          className="h-4 w-4 text-amber-700"
          strokeWidth={2}
        />

      </div>

      <span className="text-sm leading-relaxed text-gray-700">
        {children}
      </span>

    </div>
  );
}

function ConnectivityGroup({
  icon: Icon,
  title,
  rows,
}) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">

      <div className="flex items-center gap-2.5">

        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50">

          <Icon
            className="h-5 w-5 text-amber-700"
            strokeWidth={2}
          />

        </div>

        <h4 className="text-sm font-bold uppercase tracking-wide text-gray-900">
          {title}
        </h4>

      </div>

      <ul className="mt-4 space-y-3">

        {rows.map((row) => (

          <li
            key={row.name}
            className="flex items-baseline justify-between gap-4 border-b border-gray-100 pb-2.5"
          >

            <span className="text-sm font-medium text-gray-700">
              {row.name}
            </span>

            <span className="whitespace-nowrap rounded-full bg-gray-50 px-2.5 py-0.5 text-xs font-bold text-amber-700">
              {row.distance}
            </span>

          </li>

        ))}

      </ul>

    </div>
  );
}

/* ============================================================
   PROJECT DATA
============================================================ */

const PROJECT_STATS = [
  {
    value: "2.67",
    label: "Acres",
  },
  {
    value: "73%",
    label: "Open Space",
  },
  {
    value: "221",
    label: "Residences",
  },
  {
    value: "30+",
    label: "Amenities",
  },
];

const CONFIGURATIONS = [
  {
    type: "2 BHK",
    area: "1,175 Sq.Ft.",
    price: "₹93.99 Lakh*",
    highlight: "North-facing option shown in price sheet",
  },

  {
    type: "2 BHK",
    area: "1,190 Sq.Ft.",
    price: "₹94.?? Lakh*",
    highlight: "Configuration subject to availability",
  },

  {
    type: "2.5 BHK",
    area: "1,410 Sq.Ft.",
    price: "₹1.13 Cr*",
    highlight: "Indicative basic cost from supplied price sheet",
  },

  {
    type: "3 BHK",
    area: "1,675 Sq.Ft.",
    price: "Price on request",
    highlight: "Confirm latest official price",
  },

  {
    type: "3 BHK",
    area: "1,690 Sq.Ft.",
    price: "₹1.35 Cr*",
    highlight: "Indicative basic cost from supplied price sheet",
  },
];

const AMENITIES = [
  {
    icon: Waves,
    title: "Swimming Pool",
    description:
      "A landscaped pool environment designed as part of the community's leisure and wellness experience.",
  },

  {
    icon: Dumbbell,
    title: "Hydro Gym",
    description:
      "A wellness-focused facility included among the project's curated lifestyle amenities.",
  },

  {
    icon: Trophy,
    title: "Pickleball Court",
    description:
      "Dedicated recreational space for active lifestyles and community play.",
  },

  {
    icon: CircleDot,
    title: "Cricket Pitch",
    description:
      "A dedicated sports area designed for recreation and outdoor activity.",
  },

  {
    icon: Sparkles,
    title: "Amphitheatre",
    description:
      "An outdoor community space designed for gatherings and shared experiences.",
  },

  {
    icon: Trees,
    title: "Jogging Track",
    description:
      "Landscaped walking and jogging spaces integrated into the masterplan.",
  },

  {
    icon: Baby,
    title: "Children's Play Area",
    description:
      "Dedicated outdoor play spaces planned for younger residents.",
  },

  {
    icon: Users,
    title: "Pet Zone",
    description:
      "A dedicated space planned for residents with pets.",
  },

  {
    icon: Leaf,
    title: "73% Open Space",
    description:
      "A large portion of the development is planned as open and landscaped space.",
  },
];

const SIGNATURE_FEATURES = [
  {
    icon: ShieldCheck,
    title: "Vastu-Compliant Homes",
    description:
      "Homes are presented as 100% Vastu-compliant, according to the project material.",
  },

  {
    icon: LockKeyhole,
    title: "Enhanced Privacy",
    description:
      "The project highlights homes with no common walls for improved privacy.",
  },

  {
    icon: Sun,
    title: "Natural Light",
    description:
      "Layouts are designed to bring abundant natural light into everyday living spaces.",
  },

  {
    icon: Wind,
    title: "Natural Ventilation",
    description:
      "Thoughtfully planned layouts emphasize airflow and ventilation.",
  },

  {
    icon: DoorOpen,
    title: "8-Foot Main Door",
    description:
      "Residences feature a grand 8-foot main entrance door with digital door lock.",
  },

  {
    icon: Sparkles,
    title: "Premium Finishes",
    description:
      "Premium fixtures and refined finishes are part of the project's positioning.",
  },
];

const CONNECTIVITY = [
  {
    icon: Briefcase,
    title: "IT Hubs",
    rows: [
      {
        name: "ITPL",
        distance: "10 min*",
      },
      {
        name: "Ascendas Tech Park",
        distance: "10 min*",
      },
      {
        name: "Salarpuria Tech Park",
        distance: "Nearby",
      },
      {
        name: "GR Tech Park",
        distance: "Nearby",
      },
    ],
  },

  {
    icon: GraduationCap,
    title: "Schools",
    rows: [
      {
        name: "Delhi Public School",
        distance: "10 min*",
      },
      {
        name: "Winmore Academy",
        distance: "15 min*",
      },
      {
        name: "Bangalore International School",
        distance: "2 min*",
      },
      {
        name: "Whitefield Global School",
        distance: "10 min*",
      },
    ],
  },

  {
    icon: HeartPulse,
    title: "Healthcare",
    rows: [
      {
        name: "Narayana Hospital",
        distance: "10 min*",
      },
      {
        name: "Manipal Hospital",
        distance: "15 min*",
      },
      {
        name: "Vydehi Hospital",
        distance: "20 min*",
      },
      {
        name: "Sri Sathya Sai Hospital",
        distance: "15 min*",
      },
    ],
  },

  {
    icon: ShoppingBag,
    title: "Shopping",
    rows: [
      {
        name: "Park Square Mall",
        distance: "15 min*",
      },
      {
        name: "Nexus Whitefield Mall",
        distance: "15 min*",
      },
      {
        name: "Virginia Mall",
        distance: "20 min*",
      },
      {
        name: "Nexus Shantiniketan",
        distance: "20 min*",
      },
    ],
  },
];

const LIFESTYLE_POINTS = [
  {
    icon: Home,
    title: "Boutique Community",
    description:
      "A limited collection of 221 residences designed around privacy and a more intimate community experience.",
  },

  {
    icon: Trees,
    title: "Nature-Led Planning",
    description:
      "Large open areas, landscaping and recreational zones create a greener residential environment.",
  },

  {
    icon: Building2,
    title: "Two Residential Towers",
    description:
      "The development is planned across two towers with the clubhouse and recreational amenities forming the community core.",
  },

  {
    icon: HeartPulse,
    title: "Wellness-Oriented Living",
    description:
      "Swimming, hydro gym, jogging, sports and landscaped spaces support an active lifestyle.",
  },
];

const LAUNCH_BENEFITS = [
  {
    icon: BadgeCheck,
    title: "Early Launch Access",
    description:
      "Connect with the sales team for current inventory and launch-stage availability.",
  },

  {
    icon: CalendarCheck,
    title: "Private Presentation",
    description:
      "Request a detailed presentation covering layouts, specifications and pricing.",
  },

  {
    icon: Ruler,
    title: "Floor Plan Selection",
    description:
      "Explore available 2, 2.5 and 3 BHK layouts before making a decision.",
  },

  {
    icon: Navigation,
    title: "Site Visit",
    description:
      "Schedule a visit to the Soukya Road project location.",
  },
];

/* ============================================================
   PAGE
============================================================ */

export default function SanjeeviniHeliconia() {
  return (
    <div className="bg-white text-gray-900">

      <Navbar />

      {/* ======================================================
          HERO
      ====================================================== */}

      <section className="relative overflow-hidden bg-gradient-to-b from-amber-50/80 via-amber-50/20 to-white px-5 py-16 sm:px-10 sm:py-20">

        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2 lg:items-center">

          {/* LEFT */}

          <div>

            <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-bold text-amber-800">

              <Sparkles className="h-4 w-4" />

              Grand Launch · Early Access

            </div>

            <p className="mt-6 text-sm font-bold uppercase tracking-wider text-amber-700">
              Sanjeevini Group · Soukya Road · Whitefield
            </p>

            <h1 className="mt-3 text-5xl font-extrabold leading-[1.05] text-gray-900 sm:text-6xl">

              Sanjeevini{" "}

              <span className="text-amber-700">
                Heliconia
              </span>

              <br />

              Inspired Living.

            </h1>

            <p className="mt-6 max-w-xl text-base leading-relaxed text-gray-600 sm:text-lg">

              A boutique residential community on Soukya Road,
              East Bengaluru, offering thoughtfully designed 2,
              2.5 and 3 BHK homes surrounded by expansive open
              spaces, landscaped areas and curated lifestyle amenities.

            </p>

            {/* ACTIONS */}

            <div className="mt-8 flex flex-wrap gap-3">

              <a
                href="https://wa.me/message/MDG2ZSZNG6P6G1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-amber-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-amber-200 transition hover:bg-amber-700"
              >
                Contact & Enquire
              </a>

              <a
                href={buildWhatsAppLink()}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-green-500 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-green-200 transition hover:bg-green-600"
              >

                <Share2 className="h-4 w-4" />

                Enquire on WhatsApp

              </a>

              <a
                href={MAPS_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-white px-6 py-3.5 text-sm font-bold text-amber-800 transition hover:bg-amber-50"
              >

                <MapPin className="h-4 w-4" />

                View Location

              </a>

            </div>

            {/* CHECKLIST */}

            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">

              <ChecklistItem icon={Building2}>
                2 Towers
              </ChecklistItem>

              <ChecklistItem icon={Home}>
                221 Residences
              </ChecklistItem>

              <ChecklistItem icon={Trees}>
                73% Open Space
              </ChecklistItem>

              <ChecklistItem icon={Sparkles}>
                30+ Amenities
              </ChecklistItem>

            </div>

            <p className="mt-7 text-xs leading-relaxed text-gray-500">
              RERA: {RERA_NUMBER}
            </p>

          </div>

          {/* RIGHT */}

          <div className="relative">

            <Frame
              src={hero}
              alt="Sanjeevini Heliconia evening exterior"
              ratio="aspect-[4/3]"
            />

            <FloatingBadge className="-left-3 top-6 sm:-left-6">

              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Starting Configuration
              </p>

              <p className="mt-1 text-xl font-extrabold text-amber-700">
                2 BHK
              </p>

              <p className="text-xs text-gray-500">
                1,175 – 1,190 Sq.Ft.
              </p>

            </FloatingBadge>

            <FloatingBadge className="-bottom-5 -right-3 sm:-right-6">

              <p className="text-xl font-extrabold text-gray-900">
                73%
              </p>

              <p className="text-xs text-gray-500">
                Open Space
              </p>

            </FloatingBadge>

          </div>

        </div>

      </section>

      {/* ======================================================
          QUICK STATS
      ====================================================== */}

      <section className="px-5 sm:px-10">

        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 sm:grid-cols-4">

          {PROJECT_STATS.map((stat) => (
            <StatPill
              key={stat.label}
              value={stat.value}
              label={stat.label}
            />
          ))}

        </div>

      </section>

      {/* ======================================================
          OVERVIEW
      ====================================================== */}

      <section
        id="overview"
        className="px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2 lg:items-center">

          <div className="relative">

            <Frame
              src={architecture}
              alt="Sanjeevini Heliconia architecture"
              ratio="aspect-[4/3]"
            />

            <FloatingBadge className="bottom-5 left-5">

              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Project
              </p>

              <p className="mt-1 text-xl font-extrabold text-gray-900">
                2.67 Acres
              </p>

              <p className="text-xs text-gray-500">
                2 Towers · 221 Homes
              </p>

            </FloatingBadge>

          </div>

          <div>

            <p className="text-sm font-bold uppercase tracking-wide text-amber-700">
              Where Luxury Finds Its Calm
            </p>

            <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
              A quieter way to experience East Bengaluru.
            </h2>

            <p className="mt-5 text-sm leading-relaxed text-gray-600 sm:text-base">

              Sanjeevini Heliconia is positioned on Soukya Road,
              Whitefield, with a focus on refined architecture,
              privacy, natural light and a calmer residential
              environment.

            </p>

            <p className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-base">

              The development is planned across approximately
              2.67 acres with two towers and 221 exclusive
              residences. The project material highlights 73%
              open space and more than 30 lifestyle amenities.

            </p>

            <div className="mt-8 grid grid-cols-2 gap-4">

              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">

                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Residences
                </p>

                <p className="mt-1 text-2xl font-extrabold text-gray-900">
                  221
                </p>

              </div>

              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">

                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Clubhouse
                </p>

                <p className="mt-1 text-2xl font-extrabold text-gray-900">
                  7,000 Sq.Ft.
                </p>

              </div>

            </div>

          </div>

        </div>

      </section>

      {/* ======================================================
          RESIDENCES
      ====================================================== */}

      <section
        id="residences"
        className="bg-gray-50 px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto max-w-6xl">

          <p className="text-sm font-bold uppercase tracking-wide text-amber-700">
            The Residences
          </p>

          <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Homes designed around everyday ease.
          </h2>

          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-gray-600">

            Sanjeevini Heliconia offers 2, 2.5 and 3 BHK
            configurations with layouts designed around space,
            privacy, natural light and ventilation.

          </p>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">

            {CONFIGURATIONS.map((configuration, index) => (
              <ConfigurationCard
                key={`${configuration.type}-${index}`}
                {...configuration}
              />
            ))}

          </div>

          <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm leading-relaxed text-amber-900">

            <strong>Pricing note:</strong>{" "}
            The supplied price sheet shows a basic rate of
            ₹7,999/sq.ft. for the displayed units. It also lists
            parking, amenities & clubhouse, BESCOM/water/STP,
            generator & gas pipeline charges and GST separately.
            Please confirm the latest official cost sheet and
            availability before making any payment or commitment.

          </div>

        </div>

      </section>

      {/* ======================================================
          SIGNATURE FEATURES
      ====================================================== */}

      <section
        id="features"
        className="px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto max-w-6xl">

          <p className="text-sm font-bold uppercase tracking-wide text-amber-700">
            Signature Features
          </p>

          <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Designed for peace. Built for prestige.
          </h2>

          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-gray-600">

            The residences are positioned around privacy, natural
            light, ventilation, premium finishes and considered
            everyday details.

          </p>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">

            {SIGNATURE_FEATURES.map((feature) => (
              <FeatureCard
                key={feature.title}
                {...feature}
              />
            ))}

          </div>

        </div>

      </section>

      {/* ======================================================
          LIFESTYLE
      ====================================================== */}

      <section className="bg-gray-50 px-5 py-16 sm:px-10 sm:py-20">

        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2 lg:items-center">

          <div>

            <p className="text-sm font-bold uppercase tracking-wide text-amber-700">
              Elevated Living
            </p>

            <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Space, silence and sophistication.
            </h2>

            <p className="mt-5 text-sm leading-relaxed text-gray-600 sm:text-base">

              Every part of the project is positioned around creating
              a balanced residential environment — from landscaped
              spaces and recreation areas to wellness facilities and
              thoughtfully planned homes.

            </p>

            <div className="mt-8 space-y-5">

              {LIFESTYLE_POINTS.map((item) => (
                <USPItem
                  key={item.title}
                  icon={item.icon}
                >
                  <strong className="font-bold text-gray-900">
                    {item.title}
                  </strong>
                  <br />
                  {item.description}
                </USPItem>
              ))}

            </div>

          </div>

          <Frame
            src={lifestyle}
            alt="Sanjeevini Heliconia swimming pool and amenities"
            ratio="aspect-[4/3]"
          />

        </div>

      </section>

      {/* ======================================================
          AMENITIES
      ====================================================== */}

      <section
        id="amenities"
        className="px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto max-w-6xl">

          <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">

            <div>

              <p className="text-sm font-bold uppercase tracking-wide text-amber-700">
                Lifestyle Amenities
              </p>

              <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
                More space. More possibility.
              </h2>

            </div>

            <div className="rounded-full bg-amber-50 px-5 py-2.5 text-sm font-bold text-amber-800">
              30+ Experiences
            </div>

          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">

            {AMENITIES.map((amenity) => (
              <FeatureCard
                key={amenity.title}
                {...amenity}
              />
            ))}

          </div>

        </div>

      </section>

      {/* ======================================================
          MASTERPLAN
      ====================================================== */}

      <section
        id="masterplan"
        className="bg-gray-50 px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto max-w-6xl">

          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">

            <div>

              <p className="text-sm font-bold uppercase tracking-wide text-amber-700">
                Masterplan
              </p>

              <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
                Designed around experiences, not just structures.
              </h2>

              <p className="mt-5 text-sm leading-relaxed text-gray-600 sm:text-base">

                The masterplan brings together residential towers,
                clubhouse, swimming pool, children's play areas,
                pickleball, cricket, amphitheatre and jogging
                spaces within a landscaped environment.

              </p>

              <div className="mt-8 grid grid-cols-2 gap-4">

                <div className="rounded-2xl bg-white p-5 shadow-sm">

                  <p className="text-2xl font-extrabold text-amber-700">
                    73%
                  </p>

                  <p className="mt-1 text-xs text-gray-500">
                    Open Space
                  </p>

                </div>

                <div className="rounded-2xl bg-white p-5 shadow-sm">

                  <p className="text-2xl font-extrabold text-amber-700">
                    2
                  </p>

                  <p className="mt-1 text-xs text-gray-500">
                    Residential Towers
                  </p>

                </div>

              </div>

            </div>

            <Frame
              src={masterplan}
              alt="Sanjeevini Heliconia masterplan"
              ratio="aspect-[4/3]"
            />

          </div>

        </div>

      </section>

      {/* ======================================================
          CONNECTIVITY
      ====================================================== */}

      <section
        id="connectivity"
        className="px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto max-w-6xl">

          <p className="text-sm font-bold uppercase tracking-wide text-amber-700">
            Location Advantage
          </p>

          <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Close to the places that matter.
          </h2>

          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-gray-600">

            Located on Soukya Road, the project provides access to
            employment hubs, schools, healthcare facilities and
            shopping destinations around Whitefield and East Bengaluru.

          </p>

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">

            {CONNECTIVITY.map((group) => (
              <ConnectivityGroup
                key={group.title}
                {...group}
              />
            ))}

          </div>

          <p className="mt-5 text-xs text-gray-400">
            *Travel times are indicative and may vary depending on
            traffic and route conditions.
          </p>

        </div>

      </section>

      {/* ======================================================
          LOCATION
      ====================================================== */}

      <section
        id="location"
        className="bg-gray-50 px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto max-w-6xl">

          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">

            <div>

              <p className="text-sm font-bold uppercase tracking-wide text-amber-700">
                Soukya Road · Whitefield
              </p>

              <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
                A calm address with city connectivity.
              </h2>

              <p className="mt-5 text-sm leading-relaxed text-gray-600 sm:text-base">

                Sanjeevini Heliconia is located at Sy. No. 183,
                Soukya Main Road, near Soukya Health Centre,
                Nadavathi, Bengaluru Rural.

              </p>

              <div className="mt-7 space-y-4">

                <div className="flex items-start gap-3">

                  <MapPin className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />

                  <span className="text-sm font-semibold text-gray-700">
                    Sy. No. 183, Soukya Main Road,
                    Near Soukya Health Centre,
                    Nadavathi, Bengaluru Rural - 560067
                  </span>

                </div>

                <div className="flex items-center gap-3">

                  <Briefcase className="h-5 w-5 text-amber-700" />

                  <span className="text-sm font-semibold text-gray-700">
                    ITPL · Whitefield employment corridor
                  </span>

                </div>

                <div className="flex items-center gap-3">

                  <ShoppingBag className="h-5 w-5 text-amber-700" />

                  <span className="text-sm font-semibold text-gray-700">
                    Shopping & lifestyle destinations nearby
                  </span>

                </div>

              </div>

              <a
                href={MAPS_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-amber-700 px-6 py-3.5 text-sm font-bold text-white shadow-md shadow-amber-200 transition hover:bg-amber-800"
              >

                <Navigation className="h-4 w-4" />

                Open in Google Maps

              </a>

            </div>

            <Frame
              src={locationMap}
              alt="Sanjeevini Heliconia location map"
              ratio="aspect-[4/3]"
            />

          </div>

        </div>

      </section>

      {/* ======================================================
          ARCHITECTURE SHOWCASE
      ====================================================== */}

      <section className="px-5 py-16 sm:px-10 sm:py-20">

        <div className="mx-auto max-w-6xl">

          <div className="grid gap-5 md:grid-cols-2">

            <Frame
              src={gateView}
              alt="Sanjeevini Heliconia entrance"
              ratio="aspect-[4/3]"
            />

            <Frame
              src={wideElevation}
              alt="Sanjeevini Heliconia elevation"
              ratio="aspect-[4/3]"
            />

          </div>

          <div className="mt-5">

            <Frame
              src={nightView}
              alt="Sanjeevini Heliconia night view"
              ratio="aspect-[16/7]"
            />

          </div>

        </div>

      </section>

      {/* ======================================================
          POSSESSION
      ====================================================== */}

      <section className="bg-gray-50 px-5 py-16 sm:px-10 sm:py-20">

        <div className="mx-auto max-w-6xl">

          <div className="rounded-[32px] border border-amber-100 bg-white p-8 shadow-sm sm:p-10">

            <div className="grid gap-8 md:grid-cols-3 md:items-center">

              <div>

                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50">

                  <Clock3 className="h-6 w-6 text-amber-700" />

                </div>

                <h3 className="mt-5 text-xl font-extrabold text-gray-900">
                  Possession
                </h3>

              </div>

              <div className="md:col-span-2">

                <p className="text-2xl font-extrabold text-amber-700 sm:text-3xl">
                  Approximately 30 months
                </p>

                <p className="mt-2 text-sm leading-relaxed text-gray-600">

                  Your supplied project material states possession
                  is approximately 30 months from the launch date.
                  Please verify the current contractual possession
                  schedule in the official agreement and RERA records.

                </p>

              </div>

            </div>

          </div>

        </div>

      </section>

      {/* ======================================================
          LAUNCH BENEFITS
      ====================================================== */}

      <section
        id="enquire"
        className="px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto max-w-6xl">

          <div className="rounded-[32px] border border-amber-100 bg-amber-50 p-7 sm:p-10">

            <div className="grid gap-10 lg:grid-cols-2 lg:items-center">

              <div>

                <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-amber-800 shadow-sm">

                  <BadgeCheck className="h-4 w-4" />

                  Grand Launch Awaits

                </div>

                <h2 className="mt-5 text-3xl font-extrabold text-gray-900 sm:text-4xl">
                  Make your first move towards Heliconia.
                </h2>

                <p className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-base">

                  Connect with the sales team for the latest
                  availability, detailed cost sheet, floor plans,
                  specifications and site-visit information.

                </p>

              </div>

              <div className="grid gap-4 sm:grid-cols-2">

                {LAUNCH_BENEFITS.map((benefit) => {
                  const Icon = benefit.icon;

                  return (
                    <div
                      key={benefit.title}
                      className="rounded-2xl bg-white p-5 shadow-sm"
                    >

                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50">

                        <Icon
                          className="h-5 w-5 text-amber-700"
                        />

                      </div>

                      <h4 className="mt-4 text-sm font-extrabold text-gray-900">
                        {benefit.title}
                      </h4>

                      <p className="mt-2 text-xs leading-relaxed text-gray-500">
                        {benefit.description}
                      </p>

                    </div>
                  );
                })}

              </div>

            </div>

            <div className="mt-8 flex flex-wrap justify-center gap-3">

              <a
                href="https://wa.me/message/MDG2ZSZNG6P6G1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-amber-600 px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-amber-200 transition hover:bg-amber-700"
              >
                Contact & Enquire
              </a>

              <a
                href={buildWhatsAppLink()}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-green-500 px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-green-200 transition hover:bg-green-600"
              >
                <Share2 className="h-4 w-4" />

                WhatsApp Enquiry

              </a>

              <a
                href={`tel:+919945902425`}
                className="inline-flex items-center gap-2 rounded-full bg-gray-900 px-7 py-3.5 text-sm font-bold text-white transition hover:bg-gray-800"
              >
                <Phone className="h-4 w-4" />

                Call Project Team

              </a>

            </div>

          </div>

        </div>

      </section>

      {/* ======================================================
          RERA / DISCLAIMER
      ====================================================== */}

      <section className="px-5 pb-12 sm:px-10">

        <div className="mx-auto max-w-6xl rounded-2xl border border-gray-100 bg-gray-50 p-5">

          <div className="flex items-start gap-3">

            <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />

            <div>

              <p className="text-sm font-bold text-gray-900">
                RERA Registered Project
              </p>

              <p className="mt-1 text-xs leading-relaxed text-gray-500">
                RERA No. {RERA_NUMBER}. Prices, specifications,
                dimensions, availability, possession timelines,
                offers and other project details are subject to
                change. Verify current information through official
                project documents before making a financial commitment.
              </p>

              <p className="mt-2 text-xs leading-relaxed text-gray-400">
                Project visuals are artistic/representational
                impressions. Actual development, finishes,
                landscaping and amenities may vary.
              </p>

            </div>

          </div>

        </div>

      </section>

      {/* ======================================================
          FINAL CTA
      ====================================================== */}

      <section className="bg-gradient-to-r from-amber-800 via-amber-700 to-amber-600 px-5 py-16 text-center sm:px-10 sm:py-20">

        <Sparkles
          className="mx-auto h-7 w-7 text-white"
          strokeWidth={1.6}
        />

        <h2 className="mx-auto mt-4 max-w-2xl text-3xl font-extrabold text-white sm:text-4xl">

          Where Luxury
          <br />
          Finds Its Calm.

        </h2>

        <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-amber-50">

          Explore Sanjeevini Heliconia's residences,
          amenities, floor plans, pricing and launch details.

        </p>

        <div className="mt-8 flex flex-wrap justify-center gap-3">

          <a
            href="https://wa.me/message/MDG2ZSZNG6P6G1"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-amber-800 shadow-lg transition hover:bg-amber-50"
          >
            Contact & Enquire
          </a>

          <a
            href={buildWhatsAppLink()}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-green-500 px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-green-200 transition hover:bg-green-600"
          >
            <Share2 className="h-4 w-4" />

            WhatsApp Enquiry

          </a>

          <a
            href={MAPS_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 px-7 py-3.5 text-sm font-bold text-white backdrop-blur transition hover:bg-white/20"
          >

            <MapPin className="h-4 w-4" />

            View Location

          </a>

        </div>

        <p className="mt-5 text-xs font-semibold text-amber-100">

          Sanjeevini Heliconia · Soukya Road · Whitefield · Bengaluru

        </p>

      </section>

      <Footer />

      {/* ======================================================
          FLOATING WHATSAPP
      ====================================================== */}

      <a
        href={buildWhatsAppLink()}
        target="_blank"
        rel="noreferrer"
        aria-label="Enquire about Sanjeevini Heliconia on WhatsApp"
        className="fixed bottom-6 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white shadow-xl shadow-green-300/50 transition-transform hover:scale-105 sm:bottom-8 sm:right-8"
      >

        <Share2 className="h-5 w-5" />

      </a>

    </div>
  );
}